import type {
  CanonicalJudgmentPacketEntry,
  JudgmentCalendarCheckRead,
  JudgmentOptionCandidateRead,
  JudgmentPacketRead,
} from './types';

const MAX_CANONICAL_JUDGMENT_OPTIONS = 32;

export interface CanonicalJudgmentSelectionSurface {
  packet: JudgmentPacketRead;
  options: JudgmentOptionCandidateRead[];
  selectedOptionKey: string | null;
}

/**
 * A present packet owns the judgment surface even when it is not eligible for
 * a choice yet. Callers use this to avoid falling back to legacy decision
 * options for prep_needed, gate-off, or malformed canonical state.
 */
/**
 * #2422 stage 2: the canonical judgments to show, one per judgment. A Goal
 * that declares several lists them in `judgment_packets`; otherwise the single
 * `judgment_packet` is the only one.
 */
export function canonicalJudgmentEntries(
  intake: { judgment_packet?: JudgmentPacketRead | null; judgment_packets?: CanonicalJudgmentPacketEntry[] } | null | undefined,
): Array<{ condition_ref: string | null; axis: string | null; packet: JudgmentPacketRead }> {
  if (Array.isArray(intake?.judgment_packets) && intake.judgment_packets.length > 0) {
    return intake.judgment_packets.map((entry) => ({ condition_ref: entry.condition_ref, axis: entry.axis, packet: entry.packet }));
  }
  return intake?.judgment_packet ? [{ condition_ref: null, axis: null, packet: intake.judgment_packet }] : [];
}

export function hasCanonicalJudgmentPacket(packet: unknown): boolean {
  // Presence suppresses the legacy decision surface. Shape validation belongs
  // to getCanonicalJudgmentSelectionSurface, so malformed non-null values
  // cannot silently revive make_decision or decision_options.
  return packet !== null && packet !== undefined;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Return the only packet shape that is eligible for a user selection UI.
 *
 * The packet is the authority for both option identity and freshness. Labels,
 * reasons, and legacy decision fields do not decide eligibility here (#2859
 * reasons are shown by `canonicalJudgmentPreparationView`). A
 * malformed packet returns null so callers cannot accidentally offer a free
 * form or legacy fallback as a canonical selection.
 */
export function getCanonicalJudgmentSelectionSurface(
  packet: JudgmentPacketRead | null | undefined,
): CanonicalJudgmentSelectionSurface | null {
  if (!packet || (packet.readiness !== 'awaiting_user_judgment' && packet.readiness !== 'user_selected')) {
    return null;
  }
  if (!nonEmptyString(packet.selection_snapshot_token)) return null;
  if (!Array.isArray(packet.option_candidates)
    || packet.option_candidates.length === 0
    || packet.option_candidates.length > MAX_CANONICAL_JUDGMENT_OPTIONS) {
    return null;
  }

  const keys = new Set<string>();
  const options = packet.option_candidates.filter((option): option is JudgmentOptionCandidateRead => {
    if (!option || typeof option !== 'object') return false;
    if (!nonEmptyString(option.key) || !nonEmptyString(option.value)) return false;
    if (!nonEmptyString(option.label)) return false;
    if (keys.has(option.key)) return false;
    keys.add(option.key);
    return true;
  });
  if (options.length !== packet.option_candidates.length) return null;

  const selectedOptionKey = packet.selected_option_key ?? null;
  if (packet.readiness === 'user_selected') {
    if (!nonEmptyString(selectedOptionKey) || !options.some(option => option.key === selectedOptionKey)) {
      return null;
    }
  } else if (selectedOptionKey !== null && selectedOptionKey !== '') {
    return null;
  }

  return { packet, options, selectedOptionKey };
}

export function createCanonicalJudgmentInteractionIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `canonical-judgment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface CanonicalJudgmentPreparationView {
  /** One line per calendar check: the time read and what overlaps it. */
  checks: string[];
  /** Short reasons by option key, for options the preparation rules out. */
  optionReasons: Map<string, string>;
  /** The one option the preparation leaves, with why; not a choice. */
  recommendation: { label: string; reason: string | null } | null;
}

function shortTime(iso: string, lang: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(lang === 'en' ? 'en-US' : 'ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function clockTime(iso: string, lang: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleTimeString(lang === 'en' ? 'en-US' : 'ja-JP', { hour: '2-digit', minute: '2-digit' });
}

function calendarCheckLine(check: JudgmentCalendarCheckRead, lang: string): string {
  const range = `${shortTime(check.start_at, lang)}〜${clockTime(check.end_at, lang)}`;
  if (!Array.isArray(check.overlapping) || check.overlapping.length === 0) {
    // The check did not say which time it read: only what it found is known.
    if (check.time_stated === false) {
      return lang === 'en'
        ? `${range}: none of the events found overlaps (the check did not say which time it read)`
        : `${range}：見つかった予定に重なるものはありません（この時間を確かめたかは分かりません）`;
    }
    return lang === 'en' ? `${range}: nothing else on your calendar` : `${range}：重なる予定はありません`;
  }
  const events = check.overlapping.map((event) => {
    const time = `${clockTime(event.start_at, lang)}〜${clockTime(event.end_at, lang)}`;
    if (lang === 'en') return `${event.title ? `"${event.title}"` : 'an event'} (${time})`;
    return `${event.title ? `「${event.title}」` : '予定'}（${time}）`;
  });
  return lang === 'en' ? `${range}: overlaps ${events.join(', ')}` : `${range}：${events.join('、')}と重なります`;
}

/**
 * #2859 (judgment.md "Judgment preparation and option assessment"): what the
 * assistant found before the judgment, as the server derived it. The
 * recommendation only appears while the judgment is open; it never chooses.
 */
export function canonicalJudgmentPreparationView(
  surface: CanonicalJudgmentSelectionSurface,
  lang: string,
): CanonicalJudgmentPreparationView | null {
  const checks = (Array.isArray(surface.packet.dimensions) ? surface.packet.dimensions : [])
    .filter((dimension) => dimension?.key === 'calendar_check' && Array.isArray(dimension.calendar_checks))
    .flatMap((dimension) => dimension.calendar_checks ?? [])
    .map((check) => calendarCheckLine(check, lang));
  const open = surface.packet.readiness === 'awaiting_user_judgment';
  const optionReasons = new Map<string, string>();
  let recommendation: CanonicalJudgmentPreparationView['recommendation'] = null;
  if (open) {
    for (const option of surface.options) {
      if (option.recommended && !recommendation) {
        recommendation = { label: option.label, reason: nonEmptyString(option.reason) ? option.reason : null };
      } else if (!option.recommended && nonEmptyString(option.reason)) {
        optionReasons.set(option.key, option.reason);
      }
    }
  }
  if (checks.length === 0 && optionReasons.size === 0 && !recommendation) return null;
  return { checks, optionReasons, recommendation };
}
